import type { MouseEvent, JSX } from 'react';
import { useClipboard } from '@wistia/vhs';
import { Menu, MenuItem, IconButton, Icon } from '@wistia/ui';
import styled from 'styled-components';
import { trackStructEvent } from '~/utilities/snowplow';
import type { Nilable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import type { CommentPermissions } from './types';
import { useCommentContext } from './CommentContext';

const StyledCommentMenu = styled(Menu)`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
  overflow: hidden;
  padding: 6px;
`;

export const CommentContextMenu = ({
  commentId,
  permissions,
  commentHasThread,
  setShowEditForm,
  isParentComment,
  commentTimestamp,
}: {
  commentId: string;
  permissions: CommentPermissions;
  commentHasThread: boolean;
  setShowEditForm: (show: boolean) => void;
  isParentComment: boolean;
  commentTimestamp: Nilable<number>;
}): JSX.Element | null => {
  const { deleteComment, commentingView } = useCommentContext();

  const { origin, pathname } = window.location;
  const commentParam = `?comment=${encodeURIComponent(commentId)}`;
  const timestampParam = isNotNil(commentTimestamp)
    ? `&timestamp=${encodeURIComponent(commentTimestamp)}`
    : '';
  const audienceTabParam = commentingView === 'Audience' ? '&audience_tab=true' : '';
  const commentLink = `${origin}${pathname}${commentParam}${timestampParam}${audienceTabParam}`;

  const [onCopy] = useClipboard(commentLink);

  const { canEdit, canDelete } = permissions;
  const deleteText = commentHasThread ? 'Delete thread' : 'Delete';

  const hasAnyMenuItems = canEdit || canDelete || isParentComment;

  if (!hasAnyMenuItems) {
    return null;
  }

  return (
    <StyledCommentMenu
      trigger={
        <IconButton
          label="Actions"
          variant="ghost"
          colorScheme="default"
          size="sm"
        >
          <Icon type="dots-horizontal" />
        </IconButton>
      }
    >
      {canEdit && (
        <MenuItem
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            setShowEditForm(true);
          }}
        >
          Edit
        </MenuItem>
      )}
      {isParentComment && (
        <MenuItem
          onClick={() => {
            void onCopy();
          }}
        >
          Copy link
        </MenuItem>
      )}
      {canDelete && (
        <MenuItem
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            deleteComment(commentId);
            trackStructEvent({
              category: 'button',
              action: 'click',
              label: `delete-media-page-${commentingView.toLowerCase()}-comment`,
            });
          }}
          appearance="dangerous"
        >
          {deleteText}
        </MenuItem>
      )}
    </StyledCommentMenu>
  );
};
